import { Modal, ModalVariant } from '@ben/orion/modals';
import { ButtonVariant } from '@ben/orion/deprecated-button';
import Icon, { IconName } from '@ben/orion/icon';
import InlineTextButton from '@ben/orion/inline-text-button';
import Input from '@ben/orion/input';
import Typography, { TypographyVariant } from '@ben/orion/typography';
import type { IModalRef } from '@ben/shared/components/modal/modal';
import type { IModularBenefitDetail } from '@ben/shared/types/columbia';
import { useRef } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { toast } from 'react-toastify';
import styled, { css } from 'styled-components';
import { fieldCannotBeBlankErrorMessage } from '../../shared/constants';
import ModularStepComponentWrapper from './modular-step-component-wrapper';

interface IStepProps {
  index: number;
  isExpanded: boolean;
  onRemove: (index: number) => void;
  onToggle: (index: number) => void;
}

const Step = ({ index, isExpanded, onRemove, onToggle }: IStepProps) => {
  const { control } = useFormContext<IModularBenefitDetail>();

  const removeStepModalRef = useRef<IModalRef>(null);

  const handleRemoveStep = () => {
    removeStepModalRef.current?.showModal();
  };

  const handleRemoveStepConfirmation = () => {
    onRemove(index);
    toast.info(
      'Step removed successfully, but please save the benefit to reflect the changes.'
    );
  };

  return (
    <Wrapper id={`step-${index}`}>
      <StepHeaderWrapper onClick={() => onToggle(index)}>
        <StepHeader>
          <StepHeaderIconTextWrapper>
            <Chevron $isOpen={isExpanded} name={IconName.ArrowsDown} />
            <Typography variant={TypographyVariant.h3}>
              Step #{index + 1}
            </Typography>
          </StepHeaderIconTextWrapper>
          <InlineTextButton onClick={handleRemoveStep} label="Remove" />
        </StepHeader>
        <Typography variant={TypographyVariant.bodyMd1}>
          All fields below are required, unless specified optional.
        </Typography>
      </StepHeaderWrapper>
      <Controller
        name={`additionalConfig.steps.${index}.name`}
        control={control}
        rules={{ required: fieldCannotBeBlankErrorMessage }}
        render={({ field, fieldState }) => (
          <Input
            {...field}
            name="Step Name"
            width="100%"
            hasError={!!fieldState.error}
            errorMessage={fieldState.error?.message}
          />
        )}
      />
      <VisibilityContainer $isOpen={isExpanded} aria-hidden={!isExpanded}>
        <Divider />
        <ModularStepComponentWrapper
          componentControllerName={`additionalConfig.steps.${index}.components`}
        />
      </VisibilityContainer>

      <Modal
        ref={removeStepModalRef}
        title={`Remove step#${index + 1}`}
        description={`Are you sure you want to remove the step? You will also delete all the internal components.`}
        primaryText="Remove"
        secondaryText="Cancel"
        primaryVariant={ButtonVariant.error}
        onConfirm={handleRemoveStepConfirmation}
        icon={IconName.Trash}
        variant={ModalVariant.Icon}
      />
    </Wrapper>
  );
};

export default Step;

const Wrapper = styled.div`
  ${({ theme }) => css`
    display: flex;
    width: 100%;
    padding: ${theme.spacing(3)};
    flex-direction: column;
    align-items: flex-start;
    gap: ${theme.spacing(3)};
    align-self: stretch;
    border-radius: 10px;
    background: ${theme.palette.primary.bg};
  `};
`;

const StepHeaderWrapper = styled.div`
  ${({ theme }) => css`
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(1)};
  `};
`;

const StepHeader = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

const StepHeaderIconTextWrapper = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: row;
    gap: ${theme.spacing(1)};
  `};
`;

const Chevron = styled(Icon)<{ $isOpen: boolean }>`
  transform: rotate(${({ $isOpen }) => ($isOpen ? '0deg' : '-90deg')});
  cursor: pointer;
`;

const Divider = styled.div`
  ${({ theme }) => css`
    width: 100%;
    height: 1px;
    background: ${theme.palette.neutral[60]};
  `};
`;

const VisibilityContainer = styled.div<{ $isOpen: boolean }>`
  display: ${({ $isOpen }) => ($isOpen ? 'contents' : 'none')};
`;
